import { create } from 'zustand';
import { getByodAppSession, resolveWorkplaceId } from 'core/utils/byodAppSession';

/**
 * Single owner of the selected workplace for this browser tab (DATA-011).
 *
 * The value lives in memory, so switching workplace in one tab never re-scopes
 * requests or writes made from another tab. localStorage (`BYOD-app.workplace`,
 * `workplace-id`) is only the seed used when a page loads.
 *
 * React code: `useWorkplacesContext().workplaceId`.
 * Non-React code (API modules, services): `getCurrentWorkplaceId()` for reads,
 * `requireWorkplaceId()` before writes.
 */

export const NO_WORKPLACE_MESSAGE = 'No workplace selected. Choose a workplace and try again.';

export class NoWorkplaceSelectedError extends Error {
	constructor() {
		super(NO_WORKPLACE_MESSAGE);
		this.name = 'NoWorkplaceSelectedError';
	}
}

const toWorkplaceId = (value: unknown): number | null => {
	const id = Number(value);
	return Number.isInteger(id) && id > 0 ? id : null;
};

/** Workplace persisted by the last selection in any tab; used once at load. */
export const readPersistedWorkplaceId = (): number | null => {
	const fromSession = toWorkplaceId(resolveWorkplaceId(getByodAppSession()?.workplace));
	if (fromSession !== null) return fromSession;
	try {
		return toWorkplaceId(localStorage.getItem('workplace-id'));
	} catch {
		return null;
	}
};

interface WorkplaceState {
	workplaceId: number | null;
	setWorkplaceId: (id: number | null) => void;
}

export const useWorkplaceStore = create<WorkplaceState>((set) => ({
	workplaceId: readPersistedWorkplaceId(),
	setWorkplaceId: (id) => set({ workplaceId: toWorkplaceId(id) }),
}));

export const getCurrentWorkplaceId = (): number | null => useWorkplaceStore.getState().workplaceId;

export const requireWorkplaceId = (): number => {
	const id = getCurrentWorkplaceId();
	if (id === null) throw new NoWorkplaceSelectedError();
	return id;
};

export const setCurrentWorkplaceId = (id: number | null): void =>
	useWorkplaceStore.getState().setWorkplaceId(id);
